import { useCallback, useState } from 'react';
import { useToggleBookmark } from '@/hooks/mindspace/useRevision';

interface StarrableConcept {
  concept_id: string;
  is_bookmarked?: boolean | null;
}

/**
 * One starred flag per concept for a whole practice session.
 *
 * The runner's deck is FROZEN once its queue loads (`usePracticeSession`), so
 * the queue-cache patch `useToggleBookmark` makes never reaches the concepts
 * the runner renders — their `is_bookmarked` stays what it was at load. The
 * session therefore keeps its own overrides on top of that payload, keyed by
 * concept: every star rendered for a concept (the flashcard shows one per
 * face) reads the same value, and it survives moving between cards (undo).
 *
 * The write itself is `useToggleBookmark`, which optimistically patches the
 * practice-queue and quick-bytes highlight caches; on failure the override
 * rolls back here as that hook rolls back its caches.
 */
export function useConceptStars() {
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const toggleBookmark = useToggleBookmark();
  const { mutate } = toggleBookmark;

  const isStarred = useCallback(
    (concept: StarrableConcept) =>
      overrides[concept.concept_id] ?? concept.is_bookmarked ?? false,
    [overrides]
  );

  const toggle = useCallback(
    (concept: StarrableConcept) => {
      const conceptId = concept.concept_id;
      const next = !(overrides[conceptId] ?? concept.is_bookmarked ?? false);
      setOverrides(prev => ({ ...prev, [conceptId]: next }));
      mutate(
        { conceptId, nextBookmarked: next },
        {
          onError: () =>
            setOverrides(prev => ({ ...prev, [conceptId]: !next })),
        }
      );
    },
    [overrides, mutate]
  );

  return { isStarred, toggle };
}
